import { useEffect, useSyncExternalStore } from "react";
import { Inbox, Workflow, type LucideIcon } from "lucide-react";
import { Radar } from "lucide-react"; // EXP-06: Exposure's rail icon (Globe is VPN's)
// EDGE-11: Respond › Web's own icon (a published web application).
import { AppWindow } from "lucide-react";
// FW-08: Operate › Firewall's own icon (the board's brick wall).
import { BrickWall } from "lucide-react";
import { getJSON } from "../../lib/api";
import { crossTenantAccountNow, selectedTenantNow, useSelectedTenant } from "../../lib/tenantScope";

// A SURFACE APPEARS WHERE ITS PLANE EXISTS (G-09, decision 0036).
//
// /api/version reports which of the edge-security planes this deployment runs:
// per host on the engine and the agent, each plane read from its own state;
// per tenant on the control plane, from the tenant's hosts' heartbeats. The
// rail lists a plane's surface only where the plane is present, as it offers
// the lab surfaces only on lab_mode (Sidebar.tsx, surfaceOffered), and a link
// to an absent plane's surface opens a page that says the plane is not
// installed here, never an empty one (PlaneNotInstalled.tsx).
//
// ONE MAP, READ BY BOTH. PLANE_SURFACES says which plane each such surface
// needs. The rail asks it before it draws an entry and the address asks it
// before a surface opens, so the two can never disagree about where a surface
// exists. A new surface joins with one entry here; Stage 0 adds none, so the
// rail and the console are unchanged until the first one lands (BW-03).
//
// TWO QUESTIONS, NOT ONE (g6, Platform board A). A door (the rail entry, the
// palette's "go to" row, the address) and an exit (the palette's "Halt every
// playbook…") are asked separately:
//   - navOffered: the door is withdrawn only on a CONFIRMED absent (mode
//     "absent"), or when there is no report and no present was ever
//     confirmed. A plane that was present and now cannot be read keeps its
//     door with a dashed "not read" mark, so no rail row moves.
//   - exitOffered: the exit is withheld only on a confirmed absent or at the
//     estate scope (a halt is always one customer's).
// THE PALETTE AND THE LAB GATE DIFFER ON PURPOSE. A confirmed-absent plane
// still answers a palette search with one harmless row ("Traffic — not on
// this host") that opens the not-installed page; lab surfaces stay fully
// withheld, because their pages are dangerous to offer and an absent plane's
// page is safe by construction. Never merge the two gates.

/** The closed set of plane modes, as the servers spell them. */
export type PlaneMode = "absent" | "detect-only" | "enforcing" | "mixed" | "unknown";

/** One plane as the server read it. */
export interface PlaneReading {
  present: boolean;
  mode: PlaneMode;
  /** What was read, in the server's own sentence. */
  basis: string;
  /** The edge's sites and their WAF modes; the control plane names each one's host. */
  sites?: { name: string; mode: string; host?: string }[];
  /** When the server read it (engine: the probes; CP: the oldest heartbeat used). Absent from older servers. */
  readAt?: string;
  /** CP only: each live host's own reading, worst first (controlplane/planes.go PlaneHost, H-3). */
  hosts?: PlaneHostReading[];
  /**
   * CP "playbooks" only: playbooks in force acting now, of how many, from the
   * tenant's runner (controlplane/planes.go playbooksPlane). Absent when the
   * runner was not read — never a zero for "not known", and never parsed out
   * of the basis.
   */
  acting?: number;
  inForce?: number;
}

/** One live host's reading of one plane, on the control plane. */
export interface PlaneHostReading {
  host: string;
  agentId?: string;
  present: boolean;
  mode: PlaneMode;
  basis: string;
  lastSeen?: string;
}

/** /api/version's "planes": plane id → reading. */
export type PlanesReport = Readonly<Record<string, PlaneReading>>;

/** A surface that exists only where its plane does. */
export interface PlaneSurface {
  /** The surface's address: `/#<surface>` opens it. */
  surface: string;
  /** The plane id the server reports it under ("edge", "firewall", "flood", "traffic", "playbooks"). */
  plane: string;
  /** The plane's name in a sentence: "the edge", "the flood guard". */
  planeName: string;
  /** The rail entry's label. */
  label: string;
  /** The rail group the entry sits in. */
  group: "Monitor" | "Respond" | "Intelligence" | "Operate" | "Configure";
  /** The entry's own icon; no two rail entries share one (railBoardParity). */
  icon?: LucideIcon;
  /**
   * Where the surface sits: a rail entry (the default) or a tab of a parent
   * page. A tab surface is never a rail entry; its absent address lands on the
   * parent with a compact card.
   */
  placement?: { rail: PlaneSurface["group"] } | { tab: "devices" | "vpn" };
  /** The surface Back falls to when the previous page is not in this app. */
  parent?: string;
  /** What the rail mark reads: an attention state, never an inventory count. */
  attention?: "playbooks";
  /** The exit the palette offers in its destructive zone while the plane is not confirmed absent. */
  exit?: { label: string };
  /**
   * A plane only the control plane keeps (c10 fix-exposure-absent): the engine
   * registers no probe for it, so its report never names it. On the engine the
   * plane is therefore absent by construction, and its address reads "not on
   * this host" rather than "not reported" (planeAbsence).
   */
  servedBy?: "control-plane";
}

/**
 * Every plane-gated surface. Empty in Stage 0: no surface of this plan exists
 * yet. Each later surface adds itself here, with its rail entry described
 * here too, so offering it where its plane is present is one registration.
 */
const PLANE_SURFACES: PlaneSurface[] = [
  // EDGE-11: Respond › Web, the sites the edge publishes, after VPN as the
  // board's rail draws it (docs/design-v1/sys/Web.*). The engine reports
  // "edge" from its driver's own reading (api/edgestate.go edgePlane); a
  // control plane reports a tenant's edge only once agents run one (EDGE-10).
  { surface: "web", plane: "edge", planeName: "the edge", label: "Web", group: "Respond", icon: AppWindow, parent: "dashboard" },
  // G3 (SOAR-01..10): the response playbooks. Both planes report "playbooks"
  // from their runner's own state (api/playbookruns.go PlaybooksPlaneProbe;
  // controlplane/planes.go playbooksPlane).
  {
    surface: "playbooks",
    plane: "playbooks",
    planeName: "the playbook runner",
    label: "Playbooks",
    group: "Respond",
    icon: Workflow,
    parent: "dashboard",
    attention: "playbooks",
    exit: { label: "Halt every playbook…" }
  },
  // G4: the change-control queue, after Playbooks as the boards draw it. The
  // control plane reports the "approvals" plane present for a tenant it keeps
  // a queue for; the engine reports none.
  { surface: "approvals", plane: "approvals", planeName: "the change-control queue", label: "Approvals", group: "Respond", icon: Inbox, parent: "dashboard" },
  // FW-08: Operate › Firewall, the zone policy inet orao enforces
  // (docs/design-v1/sys/Firewall.*). The engine reports "firewall" from the
  // plane's own kernel reading (api/firewallplane.go firewallProbe); a control
  // plane reports it for a tenant whose hosts run the plane (FW-10).
  // "?pair=<from>-<to>#firewall" opens it with that zone pair first.
  { surface: "firewall", plane: "firewall", planeName: "the firewall", label: "Firewall", group: "Operate", icon: BrickWall, parent: "dashboard" },
  // EXP-06: Intelligence › Exposure, the customer's declared assets, their proofs,
  // authorisations and findings. Control plane only: it reports the "exposure"
  // plane for a tenant whose register it keeps (controlplane/exposure.go
  // exposurePlane); the engine reports none, so the single-tenant console has
  // no entry. "?asset=<id>#exposure" opens an asset's drill.
  {
    surface: "exposure",
    plane: "exposure",
    planeName: "the exposure register",
    label: "Exposure",
    group: "Intelligence",
    icon: Radar,
    parent: "dashboard",
    servedBy: "control-plane"
  },
];

/**
 * THE TEST SURFACE, which exists in tests and the mocked e2e only.
 *
 * The mocked e2e (web/e2e/planes.spec.ts) proves the mechanism against the
 * real console before any real surface exists, so it needs a surface to gate.
 * It names one through a window property set before the page loads, and this
 * reads it only in a development build: `import.meta.env.DEV` is false in
 * `vite build`, so the whole branch is removed from the shipped bundle.
 */
const E2E_PLANE_SURFACES = "__ORAO_E2E_PLANE_SURFACES__";
if (import.meta.env.DEV && typeof window !== "undefined") {
  const injected = (window as unknown as Record<string, unknown>)[E2E_PLANE_SURFACES];
  if (Array.isArray(injected)) {
    for (const entry of injected) {
      const parsed = asPlaneSurface(entry);
      if (parsed) PLANE_SURFACES.push(parsed);
    }
  }
}

function asPlaneSurface(value: unknown): PlaneSurface | null {
  if (!value || typeof value !== "object") return null;
  const v = value as Record<string, unknown>;
  const groups: PlaneSurface["group"][] = ["Monitor", "Respond", "Intelligence", "Operate", "Configure"];
  if (
    typeof v.surface !== "string" ||
    typeof v.plane !== "string" ||
    typeof v.planeName !== "string" ||
    typeof v.label !== "string" ||
    !groups.includes(v.group as PlaneSurface["group"])
  ) {
    return null;
  }
  return { surface: v.surface, plane: v.plane, planeName: v.planeName, label: v.label, group: v.group as PlaneSurface["group"] };
}

/**
 * Add a plane-gated surface. For tests: a real surface is a literal entry in
 * PLANE_SURFACES above. Returns the function that removes it again.
 */
export function registerPlaneSurface(entry: PlaneSurface): () => void {
  PLANE_SURFACES.push(entry);
  notify();
  return () => {
    const at = PLANE_SURFACES.indexOf(entry);
    if (at !== -1) PLANE_SURFACES.splice(at, 1);
    notify();
  };
}

/** Every plane-gated surface, in registration order. */
export function planeSurfaces(): readonly PlaneSurface[] {
  return PLANE_SURFACES;
}

/** The plane a surface needs, or undefined for a surface every deployment has. */
export function planeOfSurface(surface: string): PlaneSurface | undefined {
  return PLANE_SURFACES.find((entry) => entry.surface === surface);
}

/**
 * Whether this deployment offers `surface`, as far as its plane decides.
 *
 * A surface that needs no plane is always offered. A plane surface is offered
 * only on a report that says its plane is PRESENT: an absent plane, a plane
 * the server could not read, a server that predates the report and a report
 * not yet read all hide it — the same direction lab_mode fails in.
 */
export function planeOffered(surface: string, planes: PlanesReport | undefined): boolean {
  const entry = planeOfSurface(surface);
  if (!entry) return true;
  return planes?.[entry.plane]?.present === true;
}

const MODES: readonly PlaneMode[] = ["absent", "detect-only", "enforcing", "mixed", "unknown"];

function isCount(v: unknown): v is number {
  return typeof v === "number" && Number.isInteger(v) && v >= 0;
}

/**
 * /api/version's "planes", or undefined when the server sent none (a server
 * predating G-09). A reading the console cannot parse is kept as not present
 * and unknown, never dropped: a dropped key and an absent plane read the same
 * way at the rail, but the page has to be able to say which it was.
 */
export function normalizePlanes(value: unknown): PlanesReport | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) return undefined;
  const out: Record<string, PlaneReading> = {};
  for (const [id, raw] of Object.entries(value as Record<string, unknown>)) {
    const r = raw && typeof raw === "object" ? (raw as Record<string, unknown>) : {};
    const mode = MODES.includes(r.mode as PlaneMode) ? (r.mode as PlaneMode) : "unknown";
    const present = r.present === true;
    const reading: PlaneReading = {
      present,
      // A present plane is never "absent", and an absent one enforces nothing.
      mode: present ? (mode === "absent" ? "unknown" : mode) : mode === "absent" ? "absent" : "unknown",
      basis: typeof r.basis === "string" && r.basis ? r.basis : "the server gave no basis for this reading"
    };
    if (typeof r.read_at === "string" && r.read_at) reading.readAt = r.read_at;
    if (isCount(r.acting) && isCount(r.in_force) && r.acting <= r.in_force) {
      reading.acting = r.acting;
      reading.inForce = r.in_force;
    }
    if (Array.isArray(r.hosts)) {
      reading.hosts = r.hosts
        .filter((h): h is Record<string, unknown> => Boolean(h) && typeof h === "object")
        .map((h) => {
          const hm = MODES.includes(h.mode as PlaneMode) ? (h.mode as PlaneMode) : "unknown";
          const hp = h.present === true;
          return {
            host: typeof h.host === "string" ? h.host : "",
            ...(typeof h.agent_id === "string" && h.agent_id ? { agentId: h.agent_id } : {}),
            present: hp,
            mode: hp ? (hm === "absent" ? "unknown" : hm) : hm === "absent" ? "absent" : "unknown",
            basis: typeof h.basis === "string" ? h.basis : "",
            ...(typeof h.last_seen === "string" && h.last_seen ? { lastSeen: h.last_seen } : {})
          } satisfies PlaneHostReading;
        });
    }
    if (Array.isArray(r.sites)) {
      reading.sites = r.sites
        .filter((s): s is Record<string, unknown> => Boolean(s) && typeof s === "object")
        .map((s) => ({
          name: typeof s.name === "string" ? s.name : "",
          mode: typeof s.mode === "string" ? s.mode : "unknown",
          ...(typeof s.host === "string" && s.host ? { host: s.host } : {})
        }));
    }
    out[id] = reading;
  }
  return out;
}

// ── The report the rail reads ───────────────────────────────────────────────
//
// Read by the rail itself rather than handed down by the route, because the
// rail is mounted by every page (the SOC route, /choke, /devices, /vpn) and
// only the SOC route reads /api/version. With no plane surface registered —
// all of Stage 0 — nothing is fetched at all, so the console makes exactly the
// requests it made before G-09.

/** What the rail knows: not asked yet, asked and answered, or asked and failed. */
export type PlanesState =
  | { status: "idle" }
  | { status: "loading" }
  /**
   * This viewer's last confirmed report for the tenant, drawn while the first
   * read is on its way (g6 A "checking"); confirmed or removed in place.
   */
  | { status: "checking"; planes: PlanesReport; tenant: string | null; at: number }
  /**
   * `stale` is set once two reads in a row failed after a good one: the last
   * good report is kept (no rail row moves) and a present plane reads "not
   * read". One failure changes nothing. `since` is when the last GOOD read
   * landed: the moment the kept report was last true.
   */
  | { status: "ready"; planes: PlanesReport | undefined; tenant: string | null; stale?: { reason: string; since: number } }
  | { status: "failed"; reason: string; tenant: string | null };

let state: PlanesState = { status: "idle" };
let readAt = 0;
/**
 * Each scope's last GOOD read: when it landed and what it said, keyed by the
 * tenant it was read for ("" on the engine and at the estate). A stale report
 * says "not read since" this moment, never since the failure that made it
 * stale (fix:g6, finding 3). Keyed by tenant because on the control plane a
 * read for one customer says nothing about another: one global time made the
 * read-failed card's "Last read 14:20" name another customer's read (fix2:g6).
 */
const lastGood = new Map<string, { at: number; planes: PlanesReport | undefined }>();
/** Consecutive failed reads since the last good one (stale-while-error). */
let failures = 0;
/** "<tenant>|<plane>" for every plane read present in this session. */
const confirmedPresent = new Set<string>();
const REMEMBER_KEY = "soc.planes.v1";
/**
 * The whole /api/version body of the last good read: ONE shared version poll
 * (g6 F) feeds the planes, the reload toast and the Build block, so no two of
 * them can disagree about which build is running.
 */
let versionBody: { body: Record<string, unknown>; at: number; tenant: string | null } | null = null;
const versionListeners = new Set<() => void>();
/**
 * The read on the wire and the customer it was asked for. Keyed by tenant
 * because a provider can switch customers while a read is in flight: handing
 * the new customer the old one's read would show the old customer's rail
 * entries and not-installed verdicts until the next poll (G-09 fix).
 */
let inFlight: { tenant: string | null; promise: Promise<void> } | null = null;
const listeners = new Set<() => void>();

/** What usePlanes answers while the report it holds is another customer's. */
const LOADING: PlanesState = { status: "loading" };

/** How long one read of the report is reused. /api/version is cheap, but the rail renders often. */
const PLANES_TTL_MS = 30_000;

function notify(): void {
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

function snapshot(): PlanesState {
  return state;
}

/**
 * Record a report read elsewhere — the SOC route's own /api/version poll —
 * so the rail does not ask again for what the route already holds.
 */
export function recordPlanes(planes: PlanesReport | undefined, tenant: string | null = selectedTenantNow()): void {
  state = { status: "ready", planes, tenant };
  readAt = Date.now();
  lastGood.set(tenant ?? "", { at: readAt, planes });
  failures = 0;
  for (const [id, reading] of Object.entries(planes ?? {})) {
    if (reading.present) confirmedPresent.add(`${tenant ?? ""}|${id}`);
  }
  remember(planes, tenant);
  notify();
}

/** Record a failed read: the first keeps everything, the second marks the kept report stale. */
function recordFailure(reason: string, tenant: string | null): void {
  failures += 1;
  if (state.status === "ready" && state.tenant === tenant) {
    // `since` is the last good read, not this failure: "not read since 14:21"
    // has to name when the report on screen was true.
    if (failures >= 2) state = { ...state, stale: state.stale ?? { reason, since: lastGood.get(tenant ?? "")?.at || Date.now() } };
  } else {
    state = { status: "failed", reason, tenant };
  }
  readAt = Date.now();
  notify();
}

/** When `tenant`'s planes report was last read well (ms since the epoch), or 0 when never. */
export function lastGoodPlanesRead(tenant: string | null = selectedTenantNow()): number {
  return lastGood.get(tenant ?? "")?.at ?? 0;
}

/** `tenant`'s last good planes report and when it landed, or null when none was read in this session. */
export function lastGoodPlanesReport(tenant: string | null = selectedTenantNow()): { at: number; planes: PlanesReport | undefined } | null {
  return lastGood.get(tenant ?? "") ?? null;
}

/** Whether `plane` was read present for `tenant` earlier in this session. */
export function planeConfirmedThisSession(plane: string, tenant: string | null): boolean {
  return confirmedPresent.has(`${tenant ?? ""}|${plane}`);
}

function remember(planes: PlanesReport | undefined, tenant: string | null): void {
  try {
    if (typeof window === "undefined" || !planes) return;
    const all = JSON.parse(window.localStorage.getItem(REMEMBER_KEY) || "{}") as Record<string, unknown>;
    all[tenant ?? ""] = { at: Date.now(), planes };
    window.localStorage.setItem("soc.planes.v1", JSON.stringify(all));
  } catch {
    // Storage blocked or full: the first paint simply waits for the read.
  }
}

function remembered(tenant: string | null): { planes: PlanesReport; at: number } | null {
  try {
    if (typeof window === "undefined") return null;
    const all = JSON.parse(window.localStorage.getItem(REMEMBER_KEY) || "{}") as Record<string, { at?: unknown; planes?: unknown }>;
    const entry = all[tenant ?? ""];
    const planes = normalizePlanes(entry?.planes);
    if (!planes || typeof entry?.at !== "number") return null;
    return { planes, at: entry.at };
  } catch {
    return null;
  }
}

/** For tests: forget what was read. */
export function resetPlanesForTest(): void {
  state = { status: "idle" };
  readAt = 0;
  lastGood.clear();
  failures = 0;
  confirmedPresent.clear();
  inFlight = null;
  try {
    window.localStorage.removeItem(REMEMBER_KEY);
  } catch {
    // no storage in this environment
  }
  notify();
}

/**
 * Read /api/version's planes, naming the selected customer: on the control
 * plane the report is per tenant, and /api/version is otherwise sent unscoped
 * (lib/tenantScope.ts UNSCOPED_PATHS). The engine ignores the parameter.
 */
function readPlanes(force = false): Promise<void> {
  const tenant = selectedTenantNow();
  const fresh =
    (state.status === "ready" || state.status === "failed") && state.tenant === tenant && Date.now() - readAt < PLANES_TTL_MS;
  if (!force && fresh) return Promise.resolve();
  if (inFlight && inFlight.tenant === tenant) return inFlight.promise;
  const sameTenant = (state.status === "ready" || state.status === "failed" || state.status === "checking") && state.tenant === tenant;
  if (state.status === "idle" || !sameTenant) {
    // A tenant change reads as loading (never the previous customer's
    // report), or as this viewer's remembered report drawn "checking".
    const kept = remembered(tenant);
    state = kept ? { status: "checking", planes: kept.planes, tenant, at: kept.at } : { status: "loading" };
    failures = 0;
    notify();
  }
  const path = tenant ? `/api/version?tenant=${encodeURIComponent(tenant)}` : "/api/version";
  // An answer for a customer who is no longer selected is dropped on arrival:
  // the read for the one who is selected now is already on its way (this
  // function is asked again on every switch), and recording the old answer
  // would label it with a tenant the console has left.
  const stillSelected = () => selectedTenantNow() === tenant;
  const promise: Promise<void> = getJSON<Record<string, unknown>>(path)
    .then((body) => {
      if (!stillSelected()) return;
      if (body && typeof body === "object") {
        versionBody = { body, at: Date.now(), tenant };
        for (const listener of versionListeners) listener();
      }
      recordPlanes(normalizePlanes(body?.planes), tenant);
    })
    .catch((error: unknown) => {
      if (!stillSelected()) return;
      recordFailure(error instanceof Error ? error.message : String(error), tenant);
    })
    .finally(() => {
      if (inFlight?.promise === promise) inFlight = null;
    });
  inFlight = { tenant, promise };
  return promise;
}

/**
 * The planes report, for the rail and the address. Reads it only when some
 * surface needs a plane; otherwise answers idle and makes no request.
 *
 * A report read for another customer is answered as loading, never as this
 * customer's: on the control plane the planes are per tenant, so the previous
 * customer's report would offer surfaces, and refuse addresses, on planes the
 * selected customer may not run.
 */
export function usePlanes(): PlanesState {
  const current = useSyncExternalStore(subscribe, snapshot, snapshot);
  const tenant = useSelectedTenant();
  const needed = PLANE_SURFACES.length > 0;
  useEffect(() => {
    if (!needed) return undefined;
    void readPlanes();
    const timer = window.setInterval(() => void readPlanes(), PLANES_TTL_MS);
    // A tab coming back to the front reads again at once: the reload toast
    // and the rail should not wait out a poll the hidden tab skipped.
    const onVisible = () => {
      if (document.visibilityState === "visible") void readPlanes();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [needed, tenant]);
  return forTenant(current, tenant);
}

/** `current` if it was read for `tenant`; loading if it is another customer's. */
function forTenant(current: PlanesState, tenant: string | null): PlanesState {
  if ((current.status === "ready" || current.status === "failed" || current.status === "checking") && current.tenant !== tenant) return LOADING;
  return current;
}

/** Whether the console is at the provider's estate scope: a cross-tenant account with no customer named. */
export function estateScopeNow(tenant: string | null = selectedTenantNow()): boolean {
  return tenant === null && crossTenantAccountNow() === true;
}

/** How a door is drawn when it is offered. */
export type DoorMark = "none" | "checking" | "not-read";

/**
 * THE DOOR: whether `entry`'s rail row, palette row and address are offered,
 * and with which mark. Withdrawn only on a confirmed absent, or when there is
 * no report and no present was ever confirmed for this tenant.
 */
export function navOffered(entry: PlaneSurface, current: PlanesState, tenant: string | null = selectedTenantNow()): { offered: boolean; mark: DoorMark } {
  const confirmed = planeConfirmedThisSession(entry.plane, tenant);
  if ((current.status === "ready" || current.status === "failed" || current.status === "checking") && current.tenant !== tenant) {
    return { offered: false, mark: "none" };
  }
  switch (current.status) {
    case "idle":
    case "loading":
      return { offered: false, mark: "none" };
    case "checking":
      return current.planes[entry.plane]?.present === true ? { offered: true, mark: "checking" } : { offered: false, mark: "none" };
    case "failed":
      return confirmed ? { offered: true, mark: "not-read" } : { offered: false, mark: "none" };
    case "ready": {
      const reading = current.planes?.[entry.plane];
      if (reading?.mode === "absent") return { offered: false, mark: "none" };
      if (reading?.present) return { offered: true, mark: current.stale ? "not-read" : "none" };
      return confirmed ? { offered: true, mark: "not-read" } : { offered: false, mark: "none" };
    }
  }
}

/** Whether the plane is confirmed absent: the server read it and it is not there. */
export function planeConfirmedAbsent(entry: PlaneSurface, current: PlanesState): boolean {
  return current.status === "ready" && current.planes?.[entry.plane]?.mode === "absent";
}

/**
 * THE EXIT: whether `entry`'s exit ("Halt every playbook…") is offered.
 * Withheld on a confirmed absent and at the estate scope; otherwise it goes
 * wherever the door goes, the "not read" door included (the server refuses
 * the POST where no runner runs).
 */
export function exitOffered(entry: PlaneSurface, current: PlanesState, tenant: string | null = selectedTenantNow()): boolean {
  if (!entry.exit) return false;
  if (estateScopeNow(tenant)) return false;
  if (planeConfirmedAbsent(entry, current)) return false;
  return navOffered(entry, current, tenant).offered;
}

/** The per-viewer clock the plane chips read times in: "14:21". */
export function planeClock(iso: string | number | undefined): string {
  if (iso === undefined || iso === "") return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

/** The last good /api/version body the shared poll read, with when and for whom. */
export function useVersionBody(): { body: Record<string, unknown>; at: number; tenant: string | null } | null {
  return useSyncExternalStore(
    (listener) => {
      versionListeners.add(listener);
      return () => versionListeners.delete(listener);
    },
    () => versionBody,
    () => versionBody
  );
}

/**
 * Record a /api/version body another poll read — the SOC route's snapshot
 * loader (api.ts fetchSocSnapshot) — into the one shared reading, so the
 * planes, the reload toast and every Build block read the same body (fix:g6,
 * finding 4). `tenant` is the scope the body was read for: the snapshot sends
 * /api/version unscoped, so it is recorded only while no customer is selected
 * (the engine, or the provider's estate); a customer's planes come from the
 * scoped read here, never from an unscoped body. A body for a scope that is no
 * longer selected is dropped, as readPlanes drops one.
 */
export function recordVersionRead(body: unknown, tenant: string | null): void {
  if (!body || typeof body !== "object" || Array.isArray(body)) return;
  if (selectedTenantNow() !== tenant) return;
  versionBody = { body: body as Record<string, unknown>, at: Date.now(), tenant };
  for (const listener of versionListeners) listener();
  recordPlanes(normalizePlanes((body as Record<string, unknown>).planes), tenant);
}

/** Ask the shared poll to read now (Retry on the not-installed card). */
export function rereadPlanes(): Promise<void> {
  return readPlanes(true);
}

/** For tests: forget the version body. */
export function resetVersionBodyForTest(): void {
  versionBody = null;
  for (const listener of versionListeners) listener();
}

/** The report a state carries, if it carries one. */
export function planesOf(current: PlanesState): PlanesReport | undefined {
  return current.status === "ready" ? current.planes : undefined;
}
